import { SettingsDialog } from '@/components/settings/settings-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { useAuthStore } from '@/stores/auth-store'
import { useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'
import { BarChart3, ChevronDown, ChevronRight, ChevronUp, Forward, LogOut, MessageSquare, Phone, Settings as SettingsIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

// Standard navigation for regular devices
const standardNavigationItems = [
    {
        title: 'Aktivität',
        url: '/customer/activity',
        icon: BarChart3,
    },
    {
        title: 'Anrufe',
        url: '/customer/calls',
        icon: Phone,
    },
    {
        title: 'Wissen',
        url: '/customer/knowledge',
        icon: MessageSquare,
    },
    {
        title: 'Weiterleitung',
        url: '/customer/forward',
        icon: Forward,
    },
]

// MDS-specific navigation
const mdsNavigationItems = [
    {
        title: 'MDS Konfiguration',
        url: '/customer/mdsConfiguration',
        icon: SettingsIcon,
    },
]

export function AppSidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const [isKnowledgeExpanded, setIsKnowledgeExpanded] = useState(false)
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const { answerCategories, loadCategories, setCurrentCategory } = useCustomerStore()
    const { user, userLogout } = useAuthStore()
    const { isMdsDevice } = useDevicesStore()

    // Determine which navigation items to show based on device type
    const navigationItems = useMemo(() => {
        return isMdsDevice() ? mdsNavigationItems : standardNavigationItems
    }, [isMdsDevice])

    // Load categories when component mounts (only for non-MDS devices)
    useEffect(() => {
        if (!isMdsDevice()) {
            loadCategories()
        }
    }, [loadCategories, isMdsDevice])

    // Check if current path is knowledge related
    const isKnowledgeActive = location.pathname.startsWith('/customer/knowledge')

    // Auto-expand if we're on a knowledge page
    useEffect(() => {
        if (isKnowledgeActive) {
            setIsKnowledgeExpanded(true)
        }
    }, [isKnowledgeActive])

    // Auto-redirect to MDS configuration when MDS device is selected
    useEffect(() => {
        const isMds = isMdsDevice()
        const isOnMdsPage = location.pathname === '/customer/mdsConfiguration'
        const isOnStandardPage = location.pathname.startsWith('/customer/') && !isOnMdsPage

        if (isMds && isOnStandardPage) {
            // MDS device selected but on standard page -> redirect to MDS config
            navigate('/customer/mdsConfiguration', { replace: true })
        } else if (!isMds && isOnMdsPage) {
            // Standard device selected but on MDS page -> redirect to activity
            navigate('/customer/activity', { replace: true })
        }
    }, [isMdsDevice, location.pathname, navigate])

    const handleLogout = () => {
        userLogout()
    }

    const getUserInitials = () => {
        if (user?.firstname && user?.lastname) {
            return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase()
        }
        if (user?.email) {
            return user.email.substring(0, 2).toUpperCase()
        }
        return 'U'
    }

    return (
        <>
            <Sidebar variant="inset">
                <SidebarContent>
                    <SidebarGroup>
                        <SidebarGroupLabel className="text-base font-semibold mb-4">
                            Assistant AI
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="space-y-1">
                                {navigationItems.map((item) => {
                                    // Special handling for Wissen item (only for standard navigation)
                                    if (item.title === 'Wissen' && !isMdsDevice()) {
                                        return (
                                            <SidebarMenuItem key={item.title}>
                                                <SidebarMenuButton
                                                    asChild
                                                    isActive={isKnowledgeActive}
                                                    className="w-full justify-between"
                                                >
                                                    <Link
                                                        to={item.url}
                                                        className="flex items-center justify-between w-full px-3 py-2"
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            // Navigate to the main knowledge page
                                                            navigate(item.url)
                                                            // Toggle dropdown
                                                            setIsKnowledgeExpanded(!isKnowledgeExpanded)
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <item.icon className="h-4 w-4" />
                                                            <span>{item.title}</span>
                                                        </div>
                                                        {isKnowledgeExpanded ? (
                                                            <ChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRight className="h-4 w-4" />
                                                        )}
                                                    </Link>
                                                </SidebarMenuButton>
                                                {isKnowledgeExpanded && (
                                                    <SidebarMenuSub>
                                                        {/* Favorites category first */}
                                                        <SidebarMenuSubItem>
                                                            <SidebarMenuSubButton
                                                                asChild
                                                                isActive={location.pathname === `/customer/knowledge/favorites`}
                                                            >
                                                                <Link
                                                                    to={`/customer/knowledge/favorites`}
                                                                    className="!text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10"
                                                                >
                                                                    <span className="text-sm !text-gray-900">Favorites</span>
                                                                </Link>
                                                            </SidebarMenuSubButton>
                                                        </SidebarMenuSubItem>

                                                        {answerCategories.length === 0 ? (
                                                            <SidebarMenuSubItem>
                                                                <div className="w-full pl-8 py-2 text-sm text-muted-foreground">
                                                                    Keine Kategorien verfügbar
                                                                </div>
                                                            </SidebarMenuSubItem>
                                                        ) : (
                                                            answerCategories
                                                                .filter(cat => cat.id !== 'default' && cat.id !== 'DEFAULT')
                                                                .map((category) => (
                                                                    <SidebarMenuSubItem key={category.id}>
                                                                        <SidebarMenuSubButton
                                                                            asChild
                                                                            isActive={location.pathname === `/customer/knowledge/${category.id}`}
                                                                        >
                                                                            <Link
                                                                                to={`/customer/knowledge/${category.id}`}
                                                                                className="!text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10"
                                                                                onClick={() => setCurrentCategory(category)}
                                                                            >
                                                                                <span className="text-sm !text-gray-900">{category.title || category.name}</span>
                                                                            </Link>
                                                                        </SidebarMenuSubButton>
                                                                    </SidebarMenuSubItem>
                                                                ))
                                                        )}
                                                    </SidebarMenuSub>
                                                )}
                                            </SidebarMenuItem>
                                        )
                                    }

                                    // Regular navigation items
                                    return (
                                        <SidebarMenuItem key={item.title}>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={location.pathname === item.url}
                                                className="w-full justify-start"
                                            >
                                                <Link to={item.url} className="flex items-center gap-3 px-3 py-2">
                                                    <item.icon className="h-4 w-4" />
                                                    <span>{item.title}</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>

                <SidebarFooter>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuButton
                                        size="lg"
                                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                                    >
                                        <Avatar className="h-8 w-8 rounded-lg">
                                            <AvatarFallback className="rounded-lg bg-brand-primary text-white">
                                                {getUserInitials()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="grid flex-1 text-left text-sm leading-tight">
                                            <span className="truncate font-semibold">
                                                {user?.name || user?.email || 'Benutzer'}
                                            </span>
                                            <span className="truncate text-xs text-muted-foreground">
                                                {user?.email || ''}
                                            </span>
                                        </div>
                                        <ChevronUp className="ml-auto size-4" />
                                    </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                                    side="bottom"
                                    align="end"
                                    sideOffset={4}
                                >
                                    <DropdownMenuLabel className="p-0 font-normal">
                                        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                            <Avatar className="h-8 w-8 rounded-lg">
                                                <AvatarFallback className="rounded-lg bg-brand-primary text-white">
                                                    {getUserInitials()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="grid flex-1 text-left text-sm leading-tight">
                                                <span className="truncate font-semibold">
                                                    {user?.name || user?.email || 'Benutzer'}
                                                </span>
                                                <span className="truncate text-xs text-muted-foreground">
                                                    {user?.email || ''}
                                                </span>
                                            </div>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setIsSettingsOpen(true)}>
                                        <SettingsIcon className="mr-2 h-4 w-4" />
                                        Einstellungen
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Abmelden
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
            </Sidebar>
            <SettingsDialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
        </>
    )
}